import React, { useState } from 'react';
import { BarChart3, TrendingUp, Star, Eye, Package, DollarSign, ChevronRight, Building, ShoppingCart, Truck } from 'lucide-react';

const iconMap = {
  BarChart3,
  TrendingUp,
  Star,
  Eye,
  Package,
  DollarSign,
  Building,
  ShoppingCart,
  Truck,
};

// Dados das abas com imagens
const productTabs = [
  {
    id: 'meu-negocio',
    title: 'Meu Negócio',
    icon: 'Building',
    image: '/images/print_screen.png',
    description: 'Visão completa do seu negócio com dashboards personalizados e métricas essenciais para tomada de decisões estratégicas.',
    features: [
      'Dashboard executivo personalizado',
      'Métricas de performance',
      'Análise de crescimento do negócio',
      'Avisos personalizados'
    ]
  },
  {
    id: 'curva-abc',
    title: 'Curva ABC',
    icon: 'BarChart3',
    image: '/images/abc_print_screen.png',
    description: 'Classifique seus produtos por performance e rentabilidade. Identifique quais itens geram mais receita e otimize seu mix de produtos para maximizar lucros.',
    features: [
      'Saúde de curva ABC',
      'Análise de produtos mais vendidos',
      'Tempo de estoque do produto',
      'Relatórios de vendas'
    ]
  },
  {
    id: 'central-promocoes',
    title: 'Central de Promoções',
    icon: 'TrendingUp',
    image: '/images/cp_print_screen.png',
    description: 'Gerencie todas as suas campanhas promocionais em um só lugar. Monitore performance, ROI e otimize suas estratégias de vendas.',
    features: [
      'Gestão centralizada de promoções',
      'Monitoramento utilização',
      'Planejamento de campanhas futuras',
      'Métricas de performance'
    ]
  },
  {
    id: 'gestao-catalogo',
    title: 'Gestão de Catálogo',
    icon: 'ShoppingCart',
    image: '/images/catalog_print_screen.png',
    description: 'Organize e otimize seu catálogo de produtos com ferramentas inteligentes de gestão e análise de performance.',
    features: [
      'Gestão de produtos campeões',
      'Pontos de melhoria de produto',
      'Preço para ser vencedor',
      'Acompanhe seus catálogos em massa'
    ]
  }
];

const Solutions: React.FC = () => {
  const [activeTab, setActiveTab] = useState('meu-negocio');

  return (
    <section id="features" className="py-20 bg-white">
      <div className="container-custom">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center px-4 py-2 rounded-full bg-primary-100 text-primary-700 text-sm font-medium mb-4">
            🚀 Conheça nossas soluções
          </div>
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
            Nossas soluções preferidas para impulsionar seu negócio
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            A única plataforma de BI que você precisa.
          </p>
          <div className="mt-6 text-sm text-gray-500">
            Já processamos mais de 5 milhões de pedidos e analisamos mais de R$ 1 bilhão em transações
          </div>
        </div>

        {/* Tabs Navigation */}
        <div className="mb-12">
          <div className="flex flex-wrap justify-center gap-2 lg:gap-4">
            {productTabs.map((tab) => {
              const IconComponent = iconMap[tab.icon as keyof typeof iconMap];
              const isActive = activeTab === tab.id;
              
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center space-x-2 px-4 py-3 rounded-lg transition-all duration-200 ${
                    isActive 
                      ? 'bg-primary-500 text-white shadow-lg' 
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <IconComponent size={20} />
                  <span className="font-medium">{tab.title}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Tab Content */}
        <div className="mb-16">
          {productTabs.map((tab) => {
            const isActive = activeTab === tab.id;
            
            if (!isActive) return null;
            
            return (
              <div key={tab.id} className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                {/* Image */}
                <div className="order-2 lg:order-1">
                  <div className="relative">
                    <div className="bg-white rounded-2xl shadow-2xl overflow-hidden relative z-10">
                      <img 
                        src={tab.image} 
                        alt={`${tab.title} - BD Analytics`}
                        className="w-full h-auto object-cover"
                      />
                    </div>
                    {/* Background elements */}
                    <div className="absolute -top-4 -right-4 w-32 h-32 bg-primary-200 rounded-full opacity-20 -z-10"></div>
                    <div className="absolute -bottom-4 -left-4 w-24 h-24 bg-primary-300 rounded-full opacity-20 -z-10"></div>
                  </div>
                </div>

                {/* Content */}
                <div className="order-1 lg:order-2">
                  <div className="space-y-6">
                    <div>
                      <h3 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
                        {tab.title}
                      </h3>
                      <p className="text-lg text-gray-600 leading-relaxed">
                        {tab.description}
                      </p>
                    </div>

                    {/* Features List */}
                    <div className="space-y-3">
                      {tab.features.map((feature, index) => (
                        <div key={index} className="flex items-start space-x-3">
                          <div className="flex-shrink-0 w-6 h-6 bg-primary-100 rounded-full flex items-center justify-center mt-0.5">
                            <ChevronRight size={12} className="text-primary-600" />
                          </div>
                          <span className="text-gray-700">{feature}</span>
                        </div>
                      ))}
                    </div>

                    {/* CTA Button */}
                    <div className="pt-4">
                      <a 
                        href="https://app.bdanalytics.com.br/auth/register" 
                        className="btn-primary inline-block"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Começar a usar - 14 dias grátis
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* CTA Section */}
        <div className="text-center">
          <div className="bg-gradient-to-r from-primary-500 to-primary-600 rounded-2xl p-8 lg:p-12">
            <h3 className="text-2xl lg:text-3xl font-bold text-white mb-4">
              Pronto para transformar seus dados em vantagem competitiva?
            </h3>
            <p className="text-primary-100 text-lg mb-8 max-w-2xl mx-auto">
              Junte-se a mais de 900 empresas que já estão usando a BD Analytics para tomar decisões estratégicas baseadas em dados.
            </p>
            <a 
              href="https://app.bdanalytics.com.br/auth/register" 
              className="mx-auto inline-block bg-white text-primary-600 hover:bg-gray-50 font-semibold py-4 px-8 rounded-lg transition-colors duration-200"
              target="_blank"
              rel="noopener noreferrer"
              style={{ width: 'fit-content', maxWidth: '350px' }}
            >
              Começar a usar - 14 dias grátis
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Solutions; 